import React, {useState, useEffect} from 'react';
import {User} from './UserModals';
import {useUsersStore} from './usersStore';
import {UserTable} from './UserTable';
import {UserStats} from './UserStats';
import {AddUserModal} from './AddUserModal';
import {EditUserModal} from './EditUserModal';
import {DeleteUserDialog} from './DeleteUserDialog';
import {Button, Alert, AlertDescription, AlertTitle, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@nimbitlabs/ui';
import {RefreshCw, Info, UserPlus, Search} from 'lucide-react';
import {useAuth} from '@nimbitlabs/auth';

const UserManagement = () => {
    const {user: currentUser} = useAuth();

    const {users: allUsers = [], loading, fetchUsers, inviteUser, updateUser, removeUser} = useUsersStore();

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Local state for filters
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');

    // Filter users
    const users = React.useMemo(() => {
        return allUsers
            .filter((u) => {
                const matchesSearch =
                    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    u.email.toLowerCase().includes(searchQuery.toLowerCase());
                const matchesRole = roleFilter === 'all' || u.role === roleFilter;
                const matchesStatus = statusFilter === 'all' || u.status === statusFilter;
                return matchesSearch && matchesRole && matchesStatus;
            })
            .sort((a, b) => {
                // Sort unlocked first, then locked
                if (a.isLocked === b.isLocked) return 0;
                return a.isLocked ? 1 : -1;
            });
    }, [allUsers, searchQuery, roleFilter, statusFilter]);

    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const handleAddUser = async (userData: any) => {
        await inviteUser(userData);
    };
    const handleEditUser = (user: User) => {
        setSelectedUser(user);
        setShowEditModal(true);
    };
    const handleUpdateUser = async (userId: string, userData: any) => {
        await updateUser(userId, userData);
        setShowEditModal(false);
    };

    const handleDeleteClick = (user: User) => {
        setSelectedUser(user);
        setShowDeleteDialog(true);
    };

    const handleDeleteById = (userId: string) => {
        const user = users.find(u => u.id === userId);
        if (user) {
            handleDeleteClick(user);
        }
    };

    const handleDeleteUser = async (userId: string) => {
        try {
            await removeUser(userId);
        } catch (error) {
            console.error('Failed to delete user:', error);
        }
    };

    return (
        <div className="space-y-6">
            {/* Locked Users Alert */}
            {users.some(u => u.isLocked) && (
                <Alert className="border border-warning/15 bg-warning/5">
                    <Info className="h-4 w-4 text-warning"/>
                    <AlertTitle className="text-warning">Some users are locked</AlertTitle>
                    <AlertDescription>
                        {users.filter(u => u.isLocked).length} user(s) have view-only access due to subscription limits.
                        Your data is safe. These users can still log in but have read-only access. Admins can still
                        manage these user records.
                    </AlertDescription>
                </Alert>
            )}

            <UserStats users={users} loading={loading}/>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="flex flex-1 gap-2 w-full sm:w-auto">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search users..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                        <SelectTrigger className="w-[140px]">
                            <SelectValue placeholder="Role" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Roles</SelectItem>
                            <SelectItem value="Admin">Admin</SelectItem>
                            <SelectItem value="Editor">Editor</SelectItem>
                            <SelectItem value="Viewer">Viewer</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[140px]">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="Active">Active</SelectItem>
                            <SelectItem value="Inactive">Inactive</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <Button onClick={() => setShowAddModal(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Invite User
                </Button>
            </div>

            <UserTable
                users={users}
                onEdit={handleEditUser}
                onDelete={handleDeleteById}
                loading={loading}
            />

            <AddUserModal
                open={showAddModal}
                onOpenChange={setShowAddModal}
                onSubmit={handleAddUser}
            />

            <EditUserModal
                open={showEditModal}
                onOpenChange={setShowEditModal}
                user={selectedUser}
                onSubmit={handleUpdateUser}
            />

            <DeleteUserDialog
                open={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
                user={selectedUser}
                onConfirm={handleDeleteUser}
            />
        </div>
    );
};

export default UserManagement;